import app from 'firebase/compat/app';

import { TerpecaRankedEntity } from './results.model';

// Types of entity being considered (these are all treated as "rooms")
export enum TerpecaCategory {
  TOP_ROOM,        // IRL escape game
  TOP_COMPANY,     // Company that makes games
  TOP_ONLINE_ROOM  // Online escape game
}

// How horror-ific a room is. Sync with:
//  src/app/views/horrorlevel/horrorlevel.component.ts
//  src/app/views/horrorlevelpicker/horrorlevelpicker.component.ts
export enum HorrorLevel {
  UNKNOWN,
  NONE,
  SPOOKY,
  PASSIVE,
  ACTIVE
}

export const allHorrorLevels =
  (<(keyof typeof HorrorLevel)[]>Object.keys(HorrorLevel))
    .filter(key => isNaN(Number(key)) && HorrorLevel[key] > HorrorLevel.UNKNOWN)
    .map(key => HorrorLevel[key]);

// Basic information about a particular room (escape game) or company, used in both
// nominations (nomination.model.ts) and in confirmed room entries (below).
export interface RoomDescription {
  category?: TerpecaCategory;
  name: string;  // required for actual rooms, ignored when this is a company entity
  englishName?: string;
  company: string;
  city: string;
  state?: string;
  country: string;
  link: string;
  englishLink?: string;
  email?: string;
  languages?: string[];  // list of ISO 639-1 codes
  horrorLevel?: HorrorLevel;
}

// A confirmed room entry, kept in the Firestore 'rooms' collection,
// with basic metadata (RoomDescription) and status/workflow info.
// Note that even though it is poorly named, this same entity is also used for company entities.
export type TerpecaRoom = RoomDescription & {
  docId?: string;
  createTime?: app.firestore.Timestamp;
  partIds?: string[];  // for a combined game, the rooms it combines into a single playthrough (combined-rooms.utils.ts)

  // Nominations for this room across all years
  nominations: NominationId[];

  // Review history
  ineligibilityReason?: string;
  reviewerNotes?: string; // obsolete
  auditLogEntry?: RoomAuditLogEntry;
  auditLogHistory?: RoomAuditLogEntry[];

  // Review status decisions for each year. Unpublished current-year nominee /
  // finalist / winner flags, nomination lists, and resultsData live in the
  // roomRestricted collection (approvers and owners only). Public rooms keep
  // announced years only.
  isConfirmedOpen?: number[];
  isConfirmedEnglish?: number[];
  isApproved?: number[];
  isIneligible?: number[];
  isNominee?: number[];
  isFinalist?: number[];
  isWinner?: number[];
  // First year the room should be skipped in award processing (only the first
  // element is used). Set to the current award year if marked closed before
  // voting ends, or the next year if marked after. Shown as closed immediately.
  isPermanentlyIneligible?: number[];

  // Detailed results data keyed by year.
  resultsData?: {
    [key: number]: TerpecaRankedEntity;
  };

  // Disclosed affiliations (conflicts of interest)
  affiliatedUserIds?: string[];
};

export interface NominationId {
  year: number;
  nominationId: string;
}

export interface RoomAuditLogEntry {
  entryType: RoomAuditLogEntryType;
  uid?: string;
  name?: string;
  timestamp?: app.firestore.Timestamp;
  year?: number;
  note?: string;
}

export enum RoomStatusReason {
  UNKNOWN,
  PERMANENTLY_CLOSED,
  NOT_OPEN_30_DAYS,
  NOT_ENGLISH,
  NO_LONGER_ENGLISH,
  ENGLISH_NOT_DOCUMENTED,
  DUPLICATE,
  NOT_A_QUALIFYING_ROOM,
  OTHER
}

export const allRoomStatusReasons =
  (<(keyof typeof RoomStatusReason)[]>Object.keys(RoomStatusReason))
    .filter(key => isNaN(Number(key)) && RoomStatusReason[key] > RoomStatusReason.UNKNOWN)
    .map(key => RoomStatusReason[key]);

export const closedRoomStatusReasons = [RoomStatusReason.PERMANENTLY_CLOSED, RoomStatusReason.OTHER];

export interface RoomStatusReasonSelection {
  statusReason: RoomStatusReason;
  closedMonth?: number;
  closedYear?: number;
  customReason?: string;
  note?: string;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function getRoomStatusReasonLabel(reason: RoomStatusReason, mode?: 'disqualify' | 'closed'): string {
  switch (reason) {
    case RoomStatusReason.PERMANENTLY_CLOSED:
      return mode === 'disqualify' ?
        'Permanently closed prior to becoming eligible for this year' :
        'Permanently closed';
    case RoomStatusReason.NOT_OPEN_30_DAYS:
      return 'Not open for 30 days during the eligibility window';
    case RoomStatusReason.NOT_ENGLISH:
      return 'Not available in English';
    case RoomStatusReason.NO_LONGER_ENGLISH:
      return 'No longer available in English';
    case RoomStatusReason.ENGLISH_NOT_DOCUMENTED:
      return 'No evidence of English availability';
    case RoomStatusReason.DUPLICATE:
      return 'Duplicate listing';
    case RoomStatusReason.NOT_A_QUALIFYING_ROOM:
      return 'Not a qualifying escape room';
    case RoomStatusReason.OTHER:
      return 'Other (explain below)';
    default:
      return '';
  }
}

function closedInPhrase(month?: number, year?: number): string {
  if (!year) {
    return 'closed';
  }
  if (month && month >= 1 && month <= 12) {
    return `closed in ${MONTH_NAMES[month - 1]} ${year}`;
  }
  return `closed in ${year}`;
}

export function getRoomStatusReasonText(selection: RoomStatusReasonSelection, awardYear?: number): string {
  switch (selection.statusReason) {
    case RoomStatusReason.PERMANENTLY_CLOSED:
      return closedInPhrase(selection.closedMonth, selection.closedYear);
    case RoomStatusReason.NOT_OPEN_30_DAYS:
      return awardYear ?
        `not open for 30 days during the ${awardYear} eligibility window` :
        'not open for 30 days during the eligibility window';
    case RoomStatusReason.NOT_ENGLISH:
      return 'not available in English';
    case RoomStatusReason.NO_LONGER_ENGLISH:
      return 'no longer available in English';
    case RoomStatusReason.ENGLISH_NOT_DOCUMENTED:
      return 'no evidence of English availability';
    case RoomStatusReason.DUPLICATE:
      return 'duplicate listing';
    case RoomStatusReason.NOT_A_QUALIFYING_ROOM:
      return 'not a qualifying escape room';
    case RoomStatusReason.OTHER:
      return selection.customReason?.trim() || '';
    default:
      return '';
  }
}

function isClosedReasonText(reason?: string): boolean {
  const text = reason?.trim() || '';
  return /^closed\b/i.test(text) || /^permanently closed\b/i.test(text);
}

export function splitClosedReason(reason?: string): { closed: string; other: string } {
  const text = reason?.trim() || '';
  if (!text) {
    return { closed: '', other: '' };
  }
  if (!isClosedReasonText(text)) {
    return { closed: '', other: text };
  }
  const comma = text.indexOf(',');
  if (comma < 0) {
    return { closed: text, other: '' };
  }
  return {
    closed: text.slice(0, comma).trim(),
    other: text.slice(comma + 1).trim()
  };
}

// When a room is both closed and ineligible for a different reason, store both:
// "closed in March 2026, not available in English".
export function combineIneligibilityReason(
    existing: string | undefined, incoming: string, incomingIsClosed = false): string {
  const prior = existing?.trim() || '';
  const next = incoming?.trim() || '';
  if (!next) {
    return prior;
  }
  if (!prior || prior.toLocaleLowerCase() === next.toLocaleLowerCase()) {
    return next;
  }
  const priorClosed = isClosedReasonText(prior);
  const nextClosed = incomingIsClosed || isClosedReasonText(next);
  if (nextClosed && !priorClosed) {
    return `${next}, ${prior}`;
  }
  if (!nextClosed && priorClosed) {
    return `${prior}, ${next}`;
  }
  return next;
}

export const MONTH_OPTIONS = MONTH_NAMES.map((name, index) => ({ value: index + 1, name }));

export enum RoomAuditLogEntryType {
  LEGACY_NOTES,
  UPDATED,
  APPROVED,
  UNAPPROVED,
  DISQUALIFIED,
  UNDISQUALIFIED,
  FINALIZED,
  UNFINALIZED,
  MARKED_CLOSED,
  MARKED_REOPENED
}

export function getRoomAuditLogString(entry: RoomAuditLogEntry | undefined) {
  let description = '';
  if (!entry) {
    return description;
  }
  switch (entry.entryType) {
    case RoomAuditLogEntryType.LEGACY_NOTES:
      return entry.note;
    case RoomAuditLogEntryType.UPDATED:
      break;
    case RoomAuditLogEntryType.APPROVED:
      description = `approved`;
      break;
    case RoomAuditLogEntryType.UNAPPROVED:
      description = `unapproved`;
      break;
    case RoomAuditLogEntryType.DISQUALIFIED:
      description = `disqualified`;
      break;
    case RoomAuditLogEntryType.UNDISQUALIFIED:
      description = `undisqualified`;
      break;
    case RoomAuditLogEntryType.FINALIZED:
      description = `finalized`;
      break;
    case RoomAuditLogEntryType.UNFINALIZED:
      description = `unfinalized`;
      break;
    case RoomAuditLogEntryType.MARKED_CLOSED:
      description = `marked permanently closed`;
      break;
    case RoomAuditLogEntryType.MARKED_REOPENED:
      description = `marked reopened`;
      break;
  }
  if (entry.entryType !== RoomAuditLogEntryType.UPDATED) {
    description = `${description} for ${entry.year}`;
  }
  if (entry.note) {
    description = description.length > 0 ? `${description} - ${entry.note}` : entry.note;
  }
  if (entry.timestamp) {
    description = `${description} - ${entry.name} ${entry.timestamp.toDate().toLocaleDateString()}`;
  }
  return description;
}
